/**
 * connectorStatus — the single place raw OCPP connector state is turned
 * into something a customer can read.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────
 *
 * `station_status` rows carry two raw protocol fields straight from the
 * charger: an OCPP 1.6 `status` ("Available", "SuspendedEVSE", …) and an
 * OCPP `error_code` ("ConnectorLockFailure", "GroundFailure", …). Both
 * were previously rendered verbatim, so a driver could be shown the
 * literal string `ConnectorLockFailure` next to a connector. Protocol
 * vocabulary is not customer copy.
 *
 * Everything user-facing therefore goes through `describeConnector()`.
 * The raw values are preserved on `raw` for logging and diagnostics —
 * they are deliberately NOT part of any rendered string.
 *
 * ── SELECTABILITY IS STATUS-DRIVEN ───────────────────────────
 *
 * `Available` is the ONLY customer-selectable state.
 *
 * `Preparing` was previously treated as selectable by ConnectorPicker.
 * That was wrong, and three independent pieces of the system say so:
 *
 *   1. `deriveChargerDecision` lists 'Preparing' in IN_USE_STATES, so
 *      the charger-level gate already refuses to start ("This charger
 *      is already in use."). The picker offering a Preparing connector
 *      contradicted the very gate that would then block the tap.
 *   2. The OCPP webhook treats Preparing as a state that must map onto
 *      an EXISTING session (pending / authorized / created /
 *      payment_authorized). Preparing means "a session is already in
 *      flight on this connector", not "free to claim".
 *   3. The webhook's correction logic groups Preparing with Occupied /
 *      Unavailable as a STUCK state, reset to Available after ~120s if
 *      no active session exists — i.e. Preparing without a session is
 *      an anomaly to be corrected, not a normal selectable condition.
 *
 * So Preparing is reported as in-use and is not selectable.
 *
 * ── A FAULT CAN VETO AN "Available" STATUS ───────────────────
 *
 * A charger may report `status: Available` while simultaneously
 * reporting an `errorCode` that makes charging impossible — e.g.
 * `ConnectorLockFailure`, where the cable cannot be latched. Sending a
 * driver to that socket produces a failed start at best and an unsafe
 * connection at worst, so a BLOCKING fault overrides the status and the
 * connector is not selectable.
 *
 * Only codes with positive evidence that charging is unaffected stay
 * warnings (`ReaderFailure`, `LocalListConflict`, `WeakSignal` — see
 * NON_BLOCKING_FAULTS). Everything else, INCLUDING unrecognised vendor
 * codes, blocks: an unknown code carries no evidence that proceeding is
 * safe, and this path fails closed by design.
 */

/** OCPP's "no fault" sentinel. Treated as absence of an error. */
const NO_ERROR = 'NoError';

/** Canonical UI states. Drives copy, icon tone and selectability. */
export const CONNECTOR_UI_STATE = Object.freeze({
  AVAILABLE: 'available',
  IN_USE: 'in_use',
  RESERVED: 'reserved',
  FAULTED: 'faulted',
  UNAVAILABLE: 'unavailable',
  UNKNOWN: 'unknown',
});

/**
 * OCPP status → UI state. Keys are lower-cased so casing drift from a
 * charger vendor ("AVAILABLE", "available") cannot change behaviour.
 */
const STATUS_MAP = Object.freeze({
  available: { uiState: CONNECTOR_UI_STATE.AVAILABLE, label: 'Available', selectable: true },

  // Preparing = a session is already in flight on this connector.
  // See the header note: it is in-use, never selectable.
  preparing: { uiState: CONNECTOR_UI_STATE.IN_USE, label: 'In use', selectable: false },

  charging: { uiState: CONNECTOR_UI_STATE.IN_USE, label: 'In use', selectable: false },
  occupied: { uiState: CONNECTOR_UI_STATE.IN_USE, label: 'In use', selectable: false },
  suspendedev: { uiState: CONNECTOR_UI_STATE.IN_USE, label: 'In use', selectable: false },
  suspendedevse: { uiState: CONNECTOR_UI_STATE.IN_USE, label: 'In use', selectable: false },
  finishing: { uiState: CONNECTOR_UI_STATE.IN_USE, label: 'Finishing up', selectable: false },

  reserved: { uiState: CONNECTOR_UI_STATE.RESERVED, label: 'Reserved', selectable: false },
  faulted: { uiState: CONNECTOR_UI_STATE.FAULTED, label: 'Out of service', selectable: false },

  unavailable: { uiState: CONNECTOR_UI_STATE.UNAVAILABLE, label: 'Unavailable', selectable: false },
  disabled: { uiState: CONNECTOR_UI_STATE.UNAVAILABLE, label: 'Unavailable', selectable: false },
  busy: { uiState: CONNECTOR_UI_STATE.UNAVAILABLE, label: 'Unavailable', selectable: false },
  offline: { uiState: CONNECTOR_UI_STATE.UNAVAILABLE, label: 'Offline', selectable: false },
});

/** Anything unrecognised, missing or explicitly "unknown". */
const UNKNOWN_DESCRIPTOR = Object.freeze({
  uiState: CONNECTOR_UI_STATE.UNKNOWN,
  label: 'Status unavailable',
  selectable: false,
});

/**
 * OCPP error code → short customer-facing note. Anything not listed
 * falls back to a generic line: we never echo the raw code, because an
 * unmapped vendor code is exactly the string we must not leak.
 */
/**
 * The ONE line a driver sees for a connector they cannot use.
 *
 * This deliberately replaces a per-code table that said things like "The
 * connector lock needs attention", "Failed a safety check" and "Lost
 * contact with the vehicle". Those are maintenance findings, not customer
 * copy: they tell a driver standing at a charger nothing they can act on,
 * and they describe the state of equipment to someone with no way to fix
 * it — while quietly disclosing how the hardware is failing.
 *
 * What a driver needs is the next action. "Out of service" says the
 * connector is unusable; this says what to do about it.
 *
 * The raw OCPP code is NOT lost — it stays on the API side, where
 * assessConnectorAvailability records it in operational diagnostics that
 * never enter a response body. Operators keep the detail; drivers get the
 * instruction.
 */
const BLOCKING_FAULT_NOTE = 'Please use another connector.';

/**
 * Fault codes with positive evidence that CHARGING is unaffected, so a
 * driver may still select the connector:
 *
 *   ReaderFailure     — the RFID card reader. App-initiated (remote)
 *                       starts do not touch it.
 *   LocalListConflict — a local authorisation-list conflict. Remote
 *                       authorisation is unaffected.
 *   WeakSignal        — link quality between charger and backend.
 *                       Degrades telemetry, not the physical charge.
 *
 * Everything else — and anything unrecognised — is treated as blocking.
 * This list is intentionally short and additive-only: a code earns its
 * way onto it with evidence, never by assumption.
 */
const NON_BLOCKING_FAULTS = new Set([
  'readerfailure',
  'locallistconflict',
  'weaksignal',
]);

const norm = (value) => String(value ?? '').trim().toLowerCase();

/**
 * True when the charger reported an actual fault code. `NoError`, null,
 * empty string and whitespace all mean "no fault".
 */
export function hasFault(errorCode) {
  const e = norm(errorCode);
  return e.length > 0 && e !== norm(NO_ERROR);
}

/**
 * Customer-facing note for a raw OCPP error code.
 *
 * Returns a note ONLY for a fault that blocks the driver, and the note is
 * the same regardless of which code it was — the specific failure is
 * operational detail, not something a driver can act on.
 *
 * A NON-blocking fault returns null on purpose: that connector is still
 * usable, so telling the driver to go elsewhere would be wrong, and
 * describing the fault at all would be maintenance noise on a working
 * connector.
 *
 * Never returns the raw code under any circumstances.
 */
export function describeFault(errorCode) {
  if (!isBlockingFault(errorCode)) return null;
  return BLOCKING_FAULT_NOTE;
}

/**
 * True when the reported fault means the driver must NOT be offered this
 * connector, whatever its OCPP status says.
 *
 * Fails closed: any fault that is not explicitly known to be harmless
 * blocks, so an unmapped vendor code cannot quietly become selectable.
 */
export function isBlockingFault(errorCode) {
  if (!hasFault(errorCode)) return false;
  return !NON_BLOCKING_FAULTS.has(norm(errorCode));
}

/**
 * Normalise one raw `station_status` row into the shape the UI renders.
 *
 * @param {object} row  raw row: { connector_id | connectorId, status, error_code | errorCode }
 * @returns {{
 *   connectorId: number,        // stable backend identifier
 *   label: string,              // customer-facing name ("Connector 2")
 *   uiState: string,            // CONNECTOR_UI_STATE value
 *   statusLabel: string,        // human-readable availability
 *   selectable: boolean,
 *   note: string|null,          // friendly fault line, never a raw code
 *   raw: { status: string|null, errorCode: string|null }  // diagnostics only
 * }}
 */
export function describeConnector(row) {
  const connectorId = Number(row?.connectorId ?? row?.connector_id);
  const rawStatus = row?.status ?? null;
  const rawErrorCode = row?.errorCode ?? row?.error_code ?? null;

  // ── The BACKEND decides selectability when it has told us ──────────────
  //
  // `classification` comes from the shared availability service, which is the
  // same code path session-create runs. Preferring it is the whole point: the
  // map below cannot know whether a `Preparing` connector is somebody else's
  // session or an unclaimed cable that was just plugged in, because that
  // depends on sessions, transactions, blocks, charger liveness and how
  // recent the plug-in was — none of which the app can see.
  //
  // Deciding locally is what told a driver standing at a free charger, cable
  // already in their car, that it was "In use".
  //
  // The local map remains the fallback for a response from an API that
  // predates this field, so an older backend keeps today's behaviour rather
  // than rendering everything as unknown.
  const classification = row?.classification ?? null;
  if (classification) {
    const backendSelectable = row?.selectable === true;
    const uiState = {
      AVAILABLE: CONNECTOR_UI_STATE.AVAILABLE,
      PRECONNECTED_AVAILABLE: CONNECTOR_UI_STATE.AVAILABLE,
      IN_USE: CONNECTOR_UI_STATE.IN_USE,
      FAULTED: CONNECTOR_UI_STATE.FAULTED,
      UNAVAILABLE: CONNECTOR_UI_STATE.UNAVAILABLE,
    }[classification] || CONNECTOR_UI_STATE.UNKNOWN;

    return {
      connectorId,
      label: `Connector ${connectorId}`,
      uiState,
      // Copy comes from the backend too, so "Cable detected" and the raw OCPP
      // word can never drift apart in two codebases.
      statusLabel: row?.display_status || row?.displayStatus || 'Unavailable',
      selectable: backendSelectable,
      // The pre-connected explainer ("Your vehicle appears to be plugged
      // in…"), or a fault note, whichever the backend supplied.
      note: row?.display_detail || row?.displayDetail || describeFault(rawErrorCode),
      preconnected: classification === 'PRECONNECTED_AVAILABLE',
      raw: { status: rawStatus, errorCode: rawErrorCode, classification },
    };
  }

  const descriptor = STATUS_MAP[norm(rawStatus)] || UNKNOWN_DESCRIPTOR;

  // A blocking fault overrides an otherwise-selectable status. The
  // charger can report `Available` while the connector physically
  // cannot latch or charge; the fault is the more specific truth.
  const blockingFault = isBlockingFault(rawErrorCode);

  return {
    connectorId,
    label: `Connector ${connectorId}`,
    uiState: blockingFault ? CONNECTOR_UI_STATE.FAULTED : descriptor.uiState,
    statusLabel: blockingFault ? 'Out of service' : descriptor.label,
    selectable: descriptor.selectable && !blockingFault,
    note: describeFault(rawErrorCode),
    preconnected: false,
    // Diagnostics only. Never rendered as text — surfaced via data-*
    // attributes and console logging so support can still correlate.
    raw: { status: rawStatus, errorCode: rawErrorCode },
  };
}

/**
 * Normalise a whole `station_status` array into physical connectors.
 *
 * Connector 0 is the OCPP charge-point-wide row, not a physical socket,
 * and is always excluded. Rows without a usable integer id are dropped
 * rather than rendered as "Connector NaN" — malformed backend data must
 * not become a tappable control.
 *
 * Sorted by connector id so display order is stable regardless of the
 * order the rows arrive in. Order is presentation only: identity always
 * travels as `connectorId`, never as an array index.
 */
export function describeConnectors(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map(describeConnector)
    .filter((c) => Number.isInteger(c.connectorId) && c.connectorId > 0)
    .sort((a, b) => a.connectorId - b.connectorId);
}

/** The subset a customer is allowed to start a session on. */
export function selectableConnectors(connectors) {
  return (Array.isArray(connectors) ? connectors : []).filter((c) => c.selectable);
}

/**
 * Routing verdict for the "Get Started" tap, based on how many
 * connectors are USABLE right now (not how many are installed).
 *
 *   none   → charger cannot start a session; show the block reason
 *   single → auto-select `connectorId` and continue the existing flow
 *   choose → more than one usable connector; show the selection step
 */
export function resolveConnectorRouting(connectors) {
  const usable = selectableConnectors(connectors);
  if (usable.length === 0) {
    return { kind: 'none', connectorId: null, usable };
  }
  if (usable.length === 1) {
    return { kind: 'single', connectorId: usable[0].connectorId, usable };
  }
  return { kind: 'choose', connectorId: null, usable };
}

/**
 * Customer-facing reason a charger cannot be started, chosen from the
 * connectors' own states so the copy matches what they can see.
 */
export function noUsableConnectorMessage(connectors) {
  const list = Array.isArray(connectors) ? connectors : [];
  if (list.length === 0) {
    return "We couldn't read this charger's connectors. Please try again shortly.";
  }
  const states = new Set(list.map((c) => c.uiState));
  if (states.size === 1) {
    if (states.has(CONNECTOR_UI_STATE.IN_USE)) {
      return 'Every connector on this charger is currently in use.';
    }
    if (states.has(CONNECTOR_UI_STATE.RESERVED)) {
      return 'Every connector on this charger is currently reserved.';
    }
    if (states.has(CONNECTOR_UI_STATE.FAULTED)) {
      return 'This charger is out of service right now.';
    }
  }
  return 'No connector on this charger is available right now.';
}
